import { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import {
  ArrowRight, ArrowLeft, CheckCircle2, Lock, Loader2,
  Lightbulb, Rocket, TrendingUp, Building2, Sparkles, Calendar as CalendarIcon,
  Phone, MapPin, Trophy,
} from "lucide-react";
import { getQualAnswers } from "@/components/OnboardingModal";
import { SERVICES, STAGE_TO_SERVICE_IDS, getService } from "@/data/services";

/* ------- Stage content ------- */
const STAGES = {
  idee: {
    icon: Lightbulb,
    accent: "#FFDCA8",
    eyebrow: "Étape 01 · L'idée",
    title: "Vous avez une idée. Donnons-lui forme.",
    intro:
      "Avant tout : valider, nommer, incarner. Notre rôle est de transformer votre intuition en projet structuré, prêt à séduire vos premiers clients.",
    pitch:
      "C'est l'étape où l'on décide ce que votre marque dira, à qui, et comment. On pose un socle solide : positionnement, identité, stratégie commerciale et marketing.",
    timeline: [
      { w: "Phase 1", t: "Atelier vision, analyse de positionnement, ciblage" },
      { w: "Phase 2", t: "Naming et création d'identité visuelle" },
      { w: "Phase 3", t: "Stratégie commerciale & marketing initiale" },
    ],
    questions: [
      { key: "concept", q: "Votre concept en une phrase", placeholder: "Ex : une marketplace pour louer du matériel de sport entre particuliers." },
      { key: "cible", q: "À qui s'adresse-t-il ?", placeholder: "Ex : urbains 25-40 ans, sportifs occasionnels." },
      { key: "probleme", q: "Quel problème résolvez-vous ?", placeholder: "Décrivez le besoin réel du marché." },
      { key: "valeur", q: "Pourquoi vous, pourquoi maintenant ?", placeholder: "Votre expertise, votre obsession, votre timing." },
      { key: "budget", q: "Budget de lancement envisagé", placeholder: "Ex : à définir selon scope." },
    ],
  },
  lancement: {
    icon: Rocket,
    accent: "#A7F3D0",
    eyebrow: "Étape 02 · Lancement",
    title: "Vous venez de lancer. Accélérons.",
    intro:
      "Les premiers mois décident de tout. Il faut transformer chaque euro investi en signal — clients, traction, données, itérations.",
    pitch:
      "Vous avez prouvé que ça marche. Maintenant, on consolide votre identité, on déploie vos premiers canaux d'acquisition, et on outille votre quotidien.",
    timeline: [
      { w: "Phase 1", t: "Consolidation identité & stratégie commerciale" },
      { w: "Phase 2", t: "Mise en place des outils métier" },
      { w: "Phase 3", t: "Développement commercial : capture & campagnes" },
    ],
    questions: [
      { key: "activite", q: "Décrivez votre activité en 2 lignes", placeholder: "Produit / service, modèle économique, ce que vous vendez." },
      { key: "traction", q: "Où en êtes-vous en chiffres ?", placeholder: "Ex : 12 clients, 4K€ MRR, 200 visiteurs/mois." },
      { key: "blocage", q: "Quel est aujourd'hui votre principal blocage ?", placeholder: "Ex : pas assez de leads, conversion site faible." },
      { key: "objectif", q: "Objectif chiffré à 6 mois", placeholder: "Ex : passer de 5K à 20K€ de MRR." },
      { key: "budget", q: "Budget alloué à l'accompagnement", placeholder: "Ex : 5-15K€, ou récurrent mensuel." },
    ],
  },
  croissance: {
    icon: TrendingUp,
    accent: "#67E8F9",
    eyebrow: "Étape 03 · Croissance",
    title: "Vous croissez. Structurons pour scaler.",
    intro:
      "C'est le moment où la croissance peut casser votre entreprise — ou la propulser. On vous aide à industrialiser ce qui marche.",
    pitch:
      "Vous générez du business mais vous touchez vos limites. On bâtit l'infrastructure pour que la croissance soit prédictible : stratégie marketing, outils métier, et développement commercial structuré.",
    timeline: [
      { w: "Phase 1", t: "Audit & stratégie marketing affinée" },
      { w: "Phase 2", t: "Plateforme / outils métier sur-mesure" },
      { w: "Phase 3", t: "Pilotage & optimisation des campagnes" },
    ],
    questions: [
      { key: "activite", q: "Votre activité & vos canaux actuels", placeholder: "Ex : SaaS B2B, acquisition = LinkedIn + bouche-à-oreille." },
      { key: "equipe", q: "Taille de l'équipe & rôles", placeholder: "Ex : 8 personnes, 2 commerciaux, 3 ops, 1 marketing." },
      { key: "frein", q: "Quel est le principal frein à votre croissance ?", placeholder: "Ex : acquisition pas scalable, outils non intégrés." },
      { key: "ambition", q: "Votre ambition à 18-24 mois", placeholder: "Ex : x3 le CA, structurer pour lever des fonds." },
      { key: "budget", q: "Budget d'accompagnement envisagé", placeholder: "Ex : sur 6 mois, ou récurrent mensuel." },
    ],
  },
  etabli: {
    icon: Building2,
    accent: "#06B6D4",
    eyebrow: "Étape 04 · Entreprise établie",
    title: "Vous êtes établi. Démultiplions.",
    intro:
      "Licence, franchise, transformation digitale, nouveaux marchés : à ce stade, chaque décision a un effet de levier. On vous accompagne sur les enjeux structurants.",
    pitch:
      "Vous avez une marque, du CA, une équipe. La question est : comment dupliquer ce qui marche sans diluer la qualité ? On structure votre passage au cap supérieur.",
    timeline: [
      { w: "Phase 1", t: "Audit & stratégie de duplication" },
      { w: "Phase 2", t: "Structuration licence / franchise & outils internes" },
      { w: "Phase 3", t: "Accompagnement déploiement & comité de pilotage" },
    ],
    questions: [
      { key: "entreprise", q: "Présentez votre entreprise & son contexte", placeholder: "Activité, CA, équipe, marchés, années d'existence." },
      { key: "modele", q: "Modèle actuel & projet de duplication", placeholder: "Ex : 3 agences en propre, on veut franchiser sur 20 villes." },
      { key: "enjeu", q: "Les enjeux principaux à 12 mois", placeholder: "Ex : industrialiser les ops, ouvrir un 2ème pays." },
      { key: "ressources", q: "Ressources internes mobilisables", placeholder: "Ex : équipe ops 4 pers, budget alloué." },
      { key: "budget", q: "Budget global d'accompagnement", placeholder: "Ex : sur 12 mois." },
    ],
  },
};

// Inject services & deliverables from canonical list — no invention.
Object.entries(STAGES).forEach(([stageId, stage]) => {
  const ids = STAGE_TO_SERVICE_IDS[stageId] || [];
  stage.services = ids.map((sid) => {
    const s = getService(sid);
    return s ? { id: s.id, icon: s.icon, title: s.title, desc: s.desc } : null;
  }).filter(Boolean);
  // Deliverables = aggregated items from chosen services
  const itemsSet = new Set();
  ids.forEach((sid) => {
    const s = getService(sid);
    (s?.items || []).forEach((it) => itemsSet.add(it));
  });
  stage.deliverables = Array.from(itemsSet).slice(0, 6);
});

const STAGE_IDS = Object.keys(STAGES);

export default function StagePage() {
  const { stageId } = useParams();
  const navigate = useNavigate();
  const stage = STAGES[stageId];

  const [identity, setIdentity] = useState({ name: "", email: "", phone: "", company: "" });
  const [answers, setAnswers] = useState({});
  const [step, setStep] = useState(0); // 0 hero, 1 identité, 2..N questions, N+1 generating, N+2 brief+RDV
  const [briefId, setBriefId] = useState(null);
  const [finalBrief, setFinalBrief] = useState("");
  const [generating, setGenerating] = useState(false);

  // booking
  const [bookingType, setBookingType] = useState("phone_15m");
  const [bookingDate, setBookingDate] = useState();
  const [slots, setSlots] = useState([]);
  const [pickedSlot, setPickedSlot] = useState("");
  const [submittingRdv, setSubmittingRdv] = useState(false);
  const [unlocked, setUnlocked] = useState(false);

  const qual = useMemo(() => getQualAnswers() || {}, []);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [stageId]);

  // load slots
  useEffect(() => {
    if (!bookingDate) { setSlots([]); return; }
    const d = new Date(bookingDate).toISOString().slice(0, 10);
    api.get(`/availability/slots`, { params: { type: bookingType, date: d } })
      .then((r) => setSlots(r.data.slots || []))
      .catch(() => setSlots([]));
    setPickedSlot("");
  }, [bookingDate, bookingType]);

  // Brief generation
  useEffect(() => {
    if (!stage) return;
    const totalQ = stage.questions.length;
    if (step !== 2 + totalQ) return;
    let cancelled = false;
    (async () => {
      setGenerating(true);
      try {
        const stageLabel = stage.eyebrow;
        const projectIdea = `Stade : ${stageLabel}.
Entrepreneur : ${identity.name} — ${identity.company || "indépendant"}.
Priorité initiale (modale) : ${qual.priority || "—"} · Urgence : ${qual.urgency || "—"}.

Réponses au brief sectoriel :
${stage.questions.map((q) => `• ${q.q} → ${answers[q.key] || "—"}`).join("\n")}`;

        const r = await api.post("/ai/brief/start", {
          name: identity.name, email: identity.email, project_idea: projectIdea,
          stage: stageId, priority: qual.priority || null,
        });
        if (cancelled) return;
        setBriefId(r.data.brief_id);
        const fin = await api.post(`/ai/brief/${r.data.brief_id}/finalize`);
        if (cancelled) return;
        setFinalBrief(fin.data.final_brief);

        // capture lead
        try {
          await api.post("/leads", {
            name: identity.name, email: identity.email, phone: identity.phone, company: identity.company,
            interest: qual.priority || "both",
            source: `stage_${stageId}`,
            message: `Brief stade ${stageLabel}. Priorité: ${qual.priority || "—"}.`,
          });
        } catch {}

        setStep(3 + totalQ);
      } catch (e) {
        toast.error("Erreur lors de la génération. Réessayez.");
        setStep(2 + totalQ - 1);
      } finally { setGenerating(false); }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step]);

  if (!stage) {
    return (
      <>
        <Header />
        <main className="pt-36 sm:pt-40 pb-20 min-h-screen bg-[#FAFAFA] text-center">
          <h1 className="display text-3xl font-extrabold text-[#0A1128]">Étape introuvable</h1>
          <Button onClick={() => navigate("/")} className="mt-6 bg-[#0A1128] text-white">Retour à l'accueil</Button>
        </main>
        <Footer />
      </>
    );
  }

  const Icon = stage.icon;
  const totalQ = stage.questions.length;

  const startBrief = () => {
    if (!identity.name || !identity.email) { toast.error("Nom et email requis pour générer votre brief"); return; }
    setStep(2);
  };

  const nextQuestion = () => {
    const idx = step - 2;
    const key = stage.questions[idx]?.key;
    if (!answers[key] || answers[key].trim().length < 3) { toast.error("Merci de répondre brièvement à la question."); return; }
    if (step - 2 + 1 >= totalQ) {
      setStep(2 + totalQ); // -> generating
    } else {
      setStep(step + 1);
    }
  };

  const validateRdv = async () => {
    if (!bookingDate || !pickedSlot) return toast.error("Choisissez une date et un créneau");
    setSubmittingRdv(true);
    try {
      const d = new Date(bookingDate).toISOString().slice(0, 10);
      const payload = {
        type: bookingType, // free only (phone_15m or physical)
        name: identity.name, email: identity.email, phone: identity.phone, company: identity.company,
        preferred_date: d, preferred_time: pickedSlot,
        message: `RDV découverte — Étape : ${stage.eyebrow}. Brief #${briefId || ""}`,
      };
      await api.post("/appointments", payload);
      setUnlocked(true);
      toast.success("RDV réservé. Votre brief est désormais déverrouillé.");
      // smooth scroll to brief
      setTimeout(() => {
        const el = document.getElementById("brief-content");
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 300);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de la réservation");
    } finally { setSubmittingRdv(false); }
  };

  const downloadBrief = () => {
    const blob = new Blob([finalBrief], { type: "text/markdown;charset=utf-8" });
    const u = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = u; a.download = `cahier-des-charges-${stageId}.md`; a.click(); URL.revokeObjectURL(u);
  };

  /* Step renderer for brief form */
  const isInBrief = step >= 1 && step <= 2 + totalQ;
  const progressPct = step === 0 ? 0 : Math.min(100, Math.round(((step) / (2 + totalQ + 1)) * 100));

  return (
    <>
      <Header />
      <main className="bg-[#FAFAFA] min-h-screen" data-testid={`stage-page-${stageId}`}>
        {/* HERO */}
        <section className="relative pt-36 pb-16 sm:pt-44 sm:pb-20 overflow-hidden bg-gradient-to-br from-[#0A1128] via-[#14213d] to-[#0A1128] text-white">
          <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full opacity-20" style={{ background: stage.accent, filter: "blur(120px)" }} />
          <div className="absolute -bottom-32 -left-32 w-[420px] h-[420px] rounded-full opacity-15" style={{ background: "#06B6D4", filter: "blur(120px)" }} />
          <div className="absolute inset-0 noise opacity-30" />
          <div className="relative max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <button onClick={() => navigate("/")} className="text-xs text-white/60 hover:text-white inline-flex items-center gap-1 mb-6">
              <ArrowLeft size={14} /> Retour à l'accueil
            </button>

            {/* Stage tabs (parcours visible) */}
            <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="stage-nav">
              {STAGE_IDS.map((sid) => {
                const S = STAGES[sid]; const active = sid === stageId;
                return (
                  <button key={sid} onClick={() => navigate(`/etape/${sid}`)} data-testid={`stage-tab-${sid}`}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-bold tracking-wide transition-all ${active ? "bg-white text-[#0A1128] shadow-lg" : "bg-white/8 text-white/70 hover:bg-white/15"}`}>
                    <S.icon size={12} />
                    {S.eyebrow.split("·")[1]?.trim()}
                  </button>
                );
              })}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 text-[11px] font-extrabold tracking-[0.3em] uppercase" style={{ color: stage.accent }}>
                  <Icon size={16} /> {stage.eyebrow}
                </div>
                <h1 className="display mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold leading-[1.02]">{stage.title}</h1>
                <p className="mt-5 text-white/75 text-lg max-w-xl leading-relaxed">{stage.intro}</p>
                <p className="mt-4 text-white/55 text-sm max-w-xl">{stage.pitch}</p>

                <div className="mt-8 flex flex-wrap gap-3">
                  <Button onClick={() => { setStep(1); setTimeout(() => document.getElementById("brief")?.scrollIntoView({ behavior: "smooth" }), 50); }}
                    data-testid="stage-cta-brief" className="bg-white text-[#0A1128] hover:bg-white/90 rounded-full px-7 py-6 text-base font-bold">
                    <Sparkles size={16} className="mr-2" /> Générer mon cahier des charges
                  </Button>
                  <button onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })} className="px-6 py-3 rounded-full border border-white/30 text-white/80 hover:bg-white/10 text-sm font-semibold">
                    Voir nos services
                  </button>
                </div>
              </div>

              {/* Deliverables card */}
              <div className="lg:col-span-5">
                <div className="bg-white/8 backdrop-blur-xl border border-white/15 rounded-3xl p-6 shadow-2xl">
                  <div className="text-[11px] font-bold tracking-widest uppercase" style={{ color: stage.accent }}>Ce que vous obtenez</div>
                  <div className="mt-4 space-y-2">
                    {stage.deliverables.map((d) => (
                      <div key={d} className="flex items-start gap-2 text-sm text-white/85">
                        <CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: stage.accent }} />
                        <span>{d}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-20 sm:py-24 bg-white">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="max-w-2xl">
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Nos services pour cette étape</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl font-extrabold text-[#0A1128] leading-tight">Une offre construite pour votre stade.</h2>
              <p className="mt-3 text-[#475569]">Les leviers BESSON SOLUTIONS pertinents à votre étape, mobilisables seuls ou combinés.</p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-5" data-testid="stage-services">
              {stage.services.map((s) => (
                <div key={s.id || s.title} className="group relative bg-[#FAFAFA] rounded-2xl p-6 border border-black/8 hover:shadow-xl transition-all">
                  <div className="absolute -top-3 -left-3 w-12 h-12 rounded-xl flex items-center justify-center" style={{ background: stage.accent }}>
                    <s.icon size={20} className="text-[#0A1128]" />
                  </div>
                  <div className="mt-7">
                    <h3 className="display font-extrabold text-lg text-[#0A1128]">{s.title}</h3>
                    <p className="mt-2 text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Timeline */}
            <div className="mt-16 bg-[#0A1128] rounded-3xl p-8 sm:p-10 text-white relative overflow-hidden">
              <div className="absolute -top-20 -right-20 w-72 h-72 rounded-full opacity-20" style={{ background: stage.accent, filter: "blur(80px)" }} />
              <div className="relative">
                <div className="text-xs font-bold tracking-[0.3em] uppercase" style={{ color: stage.accent }}>Comment on travaille</div>
                <h3 className="display mt-2 text-2xl sm:text-3xl font-extrabold">Un déroulé clair, étape par étape.</h3>
                <div className="mt-8 grid grid-cols-1 sm:grid-cols-3 gap-5">
                  {stage.timeline.map((t, i) => (
                    <div key={t.w} className="bg-white/5 rounded-2xl p-5 border border-white/10">
                      <div className="display text-xl font-extrabold" style={{ color: stage.accent }}>0{i + 1}</div>
                      <div className="text-xs font-bold mt-2 text-white/70">{t.w}</div>
                      <div className="text-sm mt-1 text-white/90">{t.t}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* RDV découverte gratuit + CTA portail */}
        <section id="rdv-decouverte" className="py-20 sm:py-24 bg-gradient-to-b from-white to-[#FAFAFA]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <div className="text-xs font-bold tracking-[0.3em] uppercase text-[#06B6D4]">Étape suivante</div>
              <h2 className="display mt-3 text-3xl sm:text-4xl font-extrabold text-[#0A1128] leading-tight">Parlons-en pendant 15 minutes.</h2>
              <p className="mt-3 text-[#475569]">RDV découverte <b>gratuit</b>, par téléphone ou en physique. Sans engagement.</p>
            </div>

            <div className="mt-12 grid grid-cols-1 lg:grid-cols-5 gap-6 items-start">
              {/* RDV widget */}
              <div className="lg:col-span-3 bg-white rounded-3xl border border-black/8 p-6 shadow-sm" data-testid="stage-rdv-block">
                {unlocked ? (
                  <div className="text-center p-6">
                    <CheckCircle2 className="text-green-700 mx-auto" size={40} />
                    <div className="display text-2xl font-extrabold text-[#0A1128] mt-3">RDV envoyé !</div>
                    <p className="text-sm text-[#475569] mt-2 max-w-md mx-auto">Vous recevrez un email de confirmation après validation par l'équipe BESSON. À très vite !</p>
                    <Button onClick={() => navigate("/portail/connexion")} className="mt-5 bg-[#06B6D4] text-[#0A1128] hover:bg-[#67E8F9] font-bold rounded-full px-7 py-6">Créer mon espace entrepreneur <ArrowRight size={14} className="ml-2" /></Button>
                  </div>
                ) : (
                  <div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div><Label>Prénom & Nom *</Label><Input data-testid="stage-name" value={identity.name} onChange={(e) => setIdentity({ ...identity, name: e.target.value })} /></div>
                      <div><Label>Email *</Label><Input data-testid="stage-email" type="email" value={identity.email} onChange={(e) => setIdentity({ ...identity, email: e.target.value })} /></div>
                      <div><Label>Téléphone</Label><Input data-testid="stage-phone" value={identity.phone} onChange={(e) => setIdentity({ ...identity, phone: e.target.value })} /></div>
                      <div><Label>Entreprise (ou idée)</Label><Input data-testid="stage-company" value={identity.company} onChange={(e) => setIdentity({ ...identity, company: e.target.value })} /></div>
                    </div>

                    <div className="mt-4 flex gap-2 text-xs">
                      <button onClick={() => setBookingType("phone_15m")} data-testid="stage-type-phone" className={`flex-1 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 ${bookingType === "phone_15m" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#64748B]"}`}>
                        <Phone size={12} /> Téléphone 15 min
                      </button>
                      <button onClick={() => setBookingType("physical")} data-testid="stage-type-physical" className={`flex-1 py-2 rounded-lg font-semibold flex items-center justify-center gap-1.5 ${bookingType === "physical" ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#64748B]"}`}>
                        <MapPin size={12} /> Physique
                      </button>
                    </div>

                    <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="bg-[#FAFAFA] rounded-xl p-2">
                        <Calendar mode="single" selected={bookingDate} onSelect={setBookingDate} disabled={(d) => d < new Date(new Date().toDateString())} className="text-sm" />
                      </div>
                      <div>
                        {bookingDate ? (
                          <>
                            <div className="text-xs text-[#64748B] mb-1.5 flex items-center gap-1"><CalendarIcon size={12} /> Créneaux disponibles</div>
                            {slots.length === 0 && <div className="text-xs text-[#92400E] bg-[#FFDCA8]/40 border border-[#FFDCA8] rounded-lg p-2">Aucun créneau ce jour.</div>}
                            <div className="grid grid-cols-3 gap-1.5">
                              {slots.map((s) => (
                                <button key={s} data-testid={`stage-slot-${s}`} onClick={() => setPickedSlot(s)} className={`px-2 py-1.5 rounded text-xs font-semibold transition-colors ${pickedSlot === s ? "bg-[#06B6D4] text-[#0A1128]" : "bg-[#FAFAFA] text-[#475569] hover:bg-black/5"}`}>{s}</button>
                              ))}
                            </div>
                          </>
                        ) : (
                          <div className="text-xs text-[#64748B] p-3 bg-[#FAFAFA] rounded-xl">Sélectionnez une date à gauche pour voir les créneaux disponibles.</div>
                        )}
                      </div>
                    </div>

                    <Button onClick={validateRdv} disabled={submittingRdv || !bookingDate || !pickedSlot || !identity.name || !identity.email} data-testid="stage-validate-rdv" className="mt-5 w-full bg-[#06B6D4] hover:bg-[#67E8F9] text-[#0A1128] font-bold py-6">
                      {submittingRdv ? <><Loader2 className="animate-spin mr-2" size={14} />Réservation…</> : <>Réserver mon RDV découverte gratuit <ArrowRight size={14} className="ml-2" /></>}
                    </Button>
                    <div className="text-[10px] text-[#64748B] mt-2 text-center">Vous recevrez la confirmation par email après validation par l'équipe.</div>
                  </div>
                )}
              </div>

              {/* CTA portail */}
              <div className="lg:col-span-2 bg-[#0A1128] text-white rounded-3xl p-6 relative overflow-hidden" data-testid="stage-portal-cta">
                <div className="absolute -top-16 -right-16 w-56 h-56 rounded-full opacity-25" style={{ background: stage.accent, filter: "blur(80px)" }} />
                <div className="relative">
                  <div className="text-[11px] font-bold tracking-[0.25em] uppercase" style={{ color: stage.accent }}>Espace Entrepreneur</div>
                  <h3 className="display text-2xl font-extrabold mt-1">Créez votre espace gratuit</h3>
                  <p className="text-white/70 text-sm mt-2">Gérez vos entreprises, vos accès et soumettez de nouveaux projets en quelques clics — comme un vrai cabinet d'accompagnement.</p>
                  <ul className="mt-4 space-y-1.5 text-sm">
                    <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: stage.accent }} /><span>Une carte par entreprise (logo, accès, coordonnées)</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: stage.accent }} /><span>Suivi des prestations BESSON en temps réel</span></li>
                    <li className="flex items-start gap-2"><CheckCircle2 size={14} className="mt-0.5 flex-shrink-0" style={{ color: stage.accent }} /><span>Soumission de cahier des charges en ligne</span></li>
                  </ul>
                  <Button onClick={() => navigate("/portail/connexion")} data-testid="stage-create-portal" className="mt-5 w-full bg-white text-[#0A1128] hover:bg-white/90 font-bold py-5">
                    Créer mon espace entrepreneur <ArrowRight size={14} className="ml-2" />
                  </Button>
                </div>
              </div>
            </div>

            {/* Note premium */}
            <div className="mt-10 max-w-3xl mx-auto bg-[#FFDCA8]/30 border border-[#FFDCA8] rounded-2xl p-5 flex items-start gap-3" data-testid="stage-premium-note">
              <Trophy size={20} className="text-[#92400E] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#0A1128]">
                <b>Besoin d'un conseil ponctuel ?</b> Nos sessions de <b>consulting à l'heure</b> (1h en visio, 150€ TTC) sont réservées aux clients actifs depuis l'<a href="/portail" className="underline font-semibold">Espace Entrepreneur</a>.
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}

function FormStep({ title, subtitle, children, onPrev, onNext, nextLabel = "Suivant" }) {
  return (
    <div className="p-7 sm:p-9">
      {subtitle && <div className="text-[11px] font-bold tracking-widest uppercase text-[#06B6D4]">{subtitle}</div>}
      <h3 className="display mt-2 text-xl sm:text-2xl font-extrabold text-[#0A1128] leading-tight">{title}</h3>
      <div className="mt-5">{children}</div>
      <div className="mt-6 flex items-center justify-between">
        <Button onClick={onPrev} variant="outline" size="sm" data-testid="stage-prev"><ArrowLeft size={14} className="mr-1" />Retour</Button>
        <Button onClick={onNext} data-testid="stage-next" className="bg-[#0A1128] hover:bg-[#14213d] text-white rounded-full">{nextLabel}<ArrowRight size={14} className="ml-1" /></Button>
      </div>
    </div>
  );
}
